// TODO: rename this to opKindParts.ts or opBuilderHelpers.

import * as _ from 'lodash';
import * as Types from './model/types';
///// Functions to to create return types from input types. They
// preserve wrapped types like nullable and taggable

type Nullable<T> = T | undefined | null;

export const nullable = (
  type: Types.Type,
  applyFn: (inType: Types.Type) => Types.Type
): Types.Type => {
  if (type === 'none') {
    return 'none';
  }
  if (Types.isNullable(type)) {
    return Types.union(['none', applyFn(Types.nonNullable(type))]);
  }
  return applyFn(type);
};

export const nullableAsync = async (
  type: Types.Type,
  applyFn: (inType: Types.Type) => Promise<Types.Type>
): Promise<Types.Type> => {
  if (type === 'none') {
    return 'none';
  }
  if (Types.isNullable(type)) {
    return Types.union(['none', await applyFn(Types.nonNullable(type))]);
  }
  return applyFn(type);
};

export const nullableStrip = (type: Types.Type): Types.Type => {
  if (type === 'none') {
    return 'none';
  }
  if (Types.isNullable(type)) {
    return Types.nonNullable(type);
  }
  return type;
};

export const nullableVal = <T, R>(
  val: Nullable<T>,
  applyFn: (inVal: T) => R
): Nullable<R> => {
  if (val == null) {
    return null;
  }
  return applyFn(val);
};

export const nullableValAsync = async <T, R>(
  val: Nullable<T>,
  applyFn: (inVal: T) => Promise<Nullable<R>>
): Promise<Nullable<R>> => {
  if (val == null) {
    return null;
  }
  return applyFn(val);
};

export const skipNullable = (
  type: Types.Type,
  applyFn: (inType: Types.Type) => Types.Type
): Types.Type => {
  if (type === 'none') {
    return 'none';
  }
  if (Types.isNullable(type)) {
    return applyFn(Types.nonNullable(type));
  }
  return applyFn(type);
};

export const skipNullableAsync = async (
  type: Types.Type,
  applyFn: (inType: Types.Type) => Promise<Types.Type>
): Promise<Types.Type> => {
  if (type === 'none') {
    return 'none';
  }
  if (Types.isNullable(type)) {
    return applyFn(Types.nonNullable(type));
  }
  return applyFn(type);
};

export const taggable = (
  type: Types.Type,
  applyFn: (inType: Types.Type) => Types.Type
): Types.Type => {
  if (Types.isTaggedValue(type)) {
    return Types.taggedValue(type.tag, applyFn(type.value));
  } else if (Types.isTaggedValueUnion(type)) {
    // In the union case, apply to one member at a time. This way we get a
    // more specific union of TaggedValues instead of a TaggedValue with
    // unions for tags and values (we know which tag type goes with which
    // value).
    return Types.union(
      type.members.map(m => Types.taggedValue(m.tag, applyFn(m.value)))
    );
  }
  return applyFn(type);
};

export const taggableAsync = async (
  type: Types.Type,
  applyFn: (inType: Types.Type) => Promise<Types.Type>
): Promise<Types.Type> => {
  if (Types.isTaggedValue(type)) {
    return Types.taggedValue(type.tag, await applyFn(type.value));
  } else if (Types.isTaggedValueUnion(type)) {
    const appliedToMembers = await Promise.all(
      type.members.map(m => applyFn(m.value))
    );
    return Types.union(
      type.members.map((m, i) => Types.taggedValue(m.tag, appliedToMembers[i]))
    );
  }
  return applyFn(type);
};

export const taggableStrip = (type: Types.Type): Types.Type => {
  if (Types.isTaggedValueLike(type)) {
    return Types.taggedValueValueType(type);
  }
  return type;
};

type TaggableVal = {
  <Tag, Value, RTag, RValue>(
    val: Types.ConcreteTaggedValue<Tag, Value>,
    func: (v: Value) => Types.ConcreteTaggedValue<RTag, RValue>
  ): Types.ConcreteTaggedValue<Types.ConcreteTaggedValue<Tag, RTag>, RValue>;

  <Tag, Value, R>(
    val: Types.ConcreteTaggedValue<Tag, Value>,
    func: (v: Value) => R
  ): Types.ConcreteTaggedValue<Tag, R>;

  <V, R>(val: V, func: (v: V) => R): R;
};

export const taggableVal: TaggableVal = <T, R>(
  val: Types.Val<T>,
  applyFn: (v: Types.Val<T>) => R
) => {
  if (Types.isConcreteTaggedValue(val)) {
    return Types.concreteTaggedValue(val._tag, applyFn(val._value));
  }
  return applyFn(val);
};

type TaggableValAsync = {
  <Tag, Value, RTag, RValue>(
    val: Types.ConcreteTaggedValue<Tag, Value>,
    func: (v: Value) => Promise<Types.ConcreteTaggedValue<RTag, RValue>>
  ): Promise<
    Types.ConcreteTaggedValue<Types.ConcreteTaggedValue<Tag, RTag>, RValue>
  >;

  <Tag, Value, R>(
    val: Types.ConcreteTaggedValue<Tag, Value>,
    func: (v: Value) => Promise<R>
  ): Promise<Types.ConcreteTaggedValue<Tag, R>>;

  <V, R>(val: V, func: (v: V) => R): Promise<R>;
};

export const taggableValAsync: TaggableValAsync = async <T, R>(
  val: Types.Val<T>,
  applyFn: (v: Types.Val<T>) => Promise<R>
) => {
  if (Types.isConcreteTaggedValue(val)) {
    return Types.concreteTaggedValue(val._tag, await applyFn(val._value));
  }
  return applyFn(val);
};

type SkipTaggable = {
  <Tag extends Types.Type, Value extends Types.Type, R extends Types.Type>(
    type: Types.TaggedValueType<Tag, Value>,
    applyFn: (inType: Value, tagType?: Tag) => R
  ): R;
  <T, R extends Types.Type>(
    type: T,
    applyFn: (inType: T, tagType?: Types.Type) => R
  ): R;
};

export const skipTaggable: SkipTaggable = (
  type: Types.Type,
  applyFn: (inType: Types.Type, tagType?: Types.Type | undefined) => Types.Type
): Types.Type => {
  return Types.isTaggedValueLike(type)
    ? applyFn(Types.taggedValueValueType(type), Types.taggedValueTagType(type))
    : applyFn(type);
};

export const skipTaggableAsync = async (
  type: Types.Type,
  applyFn: (inType: Types.Type, tagType?: Types.Type) => Promise<Types.Type>
): Promise<Types.Type> => {
  return Types.isTaggedValueLike(type)
    ? applyFn(Types.taggedValueValueType(type), Types.taggedValueTagType(type))
    : applyFn(type);
};

type SkipTaggableVal = {
  <Tag, Value, R>(
    value: Types.ConcreteTaggedValue<Tag, Value>,
    applyFn: (
      inVal: Value,
      withTags: Types.ConcreteTaggedValue<Tag, Value>
    ) => R
  ): R;
  <T, R>(type: T, applyFn: (value: T) => R): R;
};

export const skipTaggableVal: SkipTaggableVal = (
  val: any,
  applyFn: (inVal: any, withTags?: any) => any
): any => {
  if (Types.isConcreteTaggedValue(val)) {
    return applyFn(val._value, val);
  }
  return applyFn(val);
};

type SkipTaggableValAsync = {
  <Tag, Value, R>(
    value: Types.ConcreteTaggedValue<Tag, Value>,
    applyFn: (
      inVal: Value,
      withTags: Types.ConcreteTaggedValue<Tag, Value>
    ) => Promise<R>
  ): Promise<R>;
  <V, R>(
    value: V,
    applyFn: (value: V, withTags: undefined) => Promise<R>
  ): Promise<R>;
};

export const skipTaggableValAsync: SkipTaggableValAsync = async (
  val: any,
  applyFn: (inVal: any, withTags?: any) => Promise<any>
): Promise<any> => {
  if (Types.isConcreteTaggedValue(val)) {
    return applyFn(val._value, val);
  }
  return applyFn(val);
};

export const nullableTaggable = <T extends Types.Type>(
  type: T,
  applyFn: (inType: Types.Type) => Types.Type
): Types.Type => {
  // Could simplify this, since taggedValue is assignable to value, order
  // doesn't matter (so we can simplify at type creation time to a fixed
  // order, and then only unwrap that order here).
  return nullable(type, t => taggable(t, u => nullable(u, applyFn)));
};

export const nullableTaggableAsync = async (
  type: Types.Type,
  applyFn: (inType: Types.Type) => Promise<Types.Type>
): Promise<Types.Type> => {
  return nullableAsync(type, t =>
    taggableAsync(t, u => nullableAsync(u, applyFn))
  );
};

export const nullableTaggableStrip = (type: Types.Type): Types.Type => {
  return nullableStrip(taggableStrip(nullableStrip(type)));
};

export const nullableTaggableVal = <T, R>(
  val: Types.Val<T>,
  applyFn: (inVal: Types.Val<T>) => R
) => {
  return nullableVal(val, t => taggableVal(t, u => nullableVal(u, applyFn)));
};

export const nullableTaggableValAsync = (
  val: any,
  applyFn: (inVal: any, withTags?: any) => Promise<any>
): Promise<any> => {
  return nullableValAsync(val, t =>
    taggableValAsync(t, u => nullableValAsync(u, v => applyFn(v)))
  );
};

export const skipNullableTaggable = <T extends Types.Type>(
  type: T,
  applyFn: (inType: Types.Type) => Types.Type
): Types.Type => {
  return skipNullable(type, t => taggable(t, u => skipNullable(u, applyFn)));
};

export const skipNullableTaggableAsync = async (
  type: Types.Type,
  applyFn: (inType: Types.Type) => Promise<Types.Type>
): Promise<Types.Type> => {
  return skipNullableAsync(type, t =>
    taggableAsync(t, u => skipNullableAsync(u, applyFn))
  );
};

export const nullableSkipTaggable = (
  type: Types.Type,
  applyFn: (inType: Types.Type, tagType?: Types.Type) => Types.Type
): Types.Type => {
  // Could simplify this, since taggedValue is assignable to value, order
  // doesn't matter (so we can simplify at type creation time to a fixed
  // order, and then only unwrap that order here).
  return nullable(type, t =>
    skipTaggable(t, (u, tagType) => nullable(u, v => applyFn(v, tagType)))
  );
};

export const nullableSkipTaggableAsync = (
  type: Types.Type,
  applyFn: (inType: Types.Type, tagType?: Types.Type) => Promise<Types.Type>
): Promise<Types.Type> => {
  return nullableAsync(type, t =>
    skipTaggableAsync(t, (u, tagType) =>
      nullableAsync(u, v => applyFn(v, tagType))
    )
  );
};

type NullableSkipTaggableValAsync = {
  <Tag, Value, R>(
    value: Nullable<Types.ConcreteTaggedValue<Tag, Value>>,
    applyFn: (
      inVal: Value,
      withTags: Types.ConcreteTaggedValue<Tag, Value>
    ) => Promise<R>
  ): Promise<R>;
  <V, R>(
    value: Nullable<V>,
    applyFn: (value: V, withTags: undefined) => Promise<R>
  ): Promise<R>;
};

export const nullableSkipTaggableValAsync: NullableSkipTaggableValAsync = (
  val: any,
  applyFn: (inVal: any, withTags: any) => Promise<any>
): Promise<any> => {
  return nullableValAsync(val, t =>
    skipTaggableValAsync(t, (u, uWithTags) =>
      nullableValAsync(u, v => applyFn(v, uWithTags))
    )
  );
};

export const mappable = (
  type: Types.Type,
  applyFn: (inType: Types.Type) => Types.Type
): Types.Type => {
  if (Types.isList(type)) {
    return Types.list(
      applyFn(Types.listObjectType(type)),
      type.minLength,
      type.maxLength
    );
  }
  return applyFn(type);
};

export const mappableAsync = async (
  type: Types.Type,
  applyFn: (inType: Types.Type) => Promise<Types.Type>
): Promise<Types.Type> => {
  if (Types.isList(type)) {
    return Types.list(
      await applyFn(Types.listObjectType(type)),
      type.minLength,
      type.maxLength
    );
  }
  return applyFn(type);
};

export const mappableStrip = (type: Types.Type): Types.Type => {
  if (Types.isList(type)) {
    return Types.listObjectType(type);
  }
  return type;
};

export const mappableVal = (v: any, applyFn: (v: any) => any): any => {
  if (_.isArray(v)) {
    return v.map(t => applyFn(t));
  }
  return applyFn(v);
};

export const mappableValAsync = (
  v: any,
  applyFn: (
    v: any,
    didMap: boolean,
    mapIndex: number | undefined
  ) => Promise<any>
): Promise<any> => {
  if (_.isArray(v)) {
    return Promise.all(v.map((t, i) => applyFn(t, true, i)));
  }
  return applyFn(v, false, undefined);
};

export const mappableNullable = (
  type: Types.Type,
  applyFn: (inType: Types.Type) => Types.Type
): Types.Type => {
  return nullable(type, t => mappable(t, u => nullable(u, applyFn)));
};

export const mappableNullableVal = (
  val: any,
  applyFn: (inVal: any) => any
): any => {
  return nullableVal(val, t => mappableVal(t, u => nullableVal(u, applyFn)));
};

export const mappableNullableValAsync = (
  val: any,
  applyFn: (inVal: any) => Promise<any>
): Promise<any> => {
  return nullableValAsync(val, t =>
    mappableValAsync(t, u => nullableValAsync(u, applyFn))
  );
};

export const mappableTaggable = (
  type: Types.Type,
  applyFn: (inType: Types.Type) => Types.Type
): Types.Type => {
  return taggable(type, t => mappable(t, u => taggable(u, applyFn)));
};

export const mappableTaggableVal = (
  val: any,
  applyFn: (inVal: any) => any
): any => {
  return taggableVal(val, t => mappableVal(t, u => taggableVal(u, applyFn)));
};

export const mappableTaggableValAsync = (
  val: any,
  applyFn: (inVal: any) => Promise<any>
): Promise<any> => {
  return taggableValAsync(val, t =>
    mappableValAsync(t, u => taggableValAsync(u, applyFn))
  );
};

export const mappableNullableTaggable = (
  type: Types.Type,
  applyFn: (inType: Types.Type) => Types.Type
): Types.Type => {
  return nullableTaggable(type, t =>
    mappable(t, u => nullableTaggable(u, applyFn))
  );
};

export const mappableNullableTaggableAsync = (
  type: Types.Type,
  applyFn: (inType: Types.Type) => Promise<Types.Type>
): Promise<Types.Type> => {
  return nullableTaggableAsync(type, t =>
    mappableAsync(t, u => nullableTaggableAsync(u, applyFn))
  );
};

export const mappableNullableTaggableStrip = (type: Types.Type): Types.Type => {
  return nullableTaggableStrip(mappableStrip(nullableTaggableStrip(type)));
};

export const mappableNullableTaggableVal = (
  val: any,
  applyFn: (inVal: any) => any
): any => {
  return nullableTaggableVal(val, t =>
    mappableVal(t, u => nullableTaggableVal(u, applyFn))
  );
};

export const mappableNullableTaggableValAsync = async (
  val: any,
  applyFn: (inVal: any) => Promise<any>
): Promise<any> => {
  // This function is a major cg hotspot. It is manual implementation of this
  // logic:
  // return nullableTaggableValAsync(val, t =>
  //   mappableValAsync(t, u => nullableTaggableValAsync(u, v => applyFn(v)))
  // );
  // Using the above logic instead results in a ton of allocation and gc
  // activity, because of all of the anonymous functions.
  if (val == null) {
    // nullable
    return val;
  }
  let outerTag: any;
  if (Types.isConcreteTaggedValue(val)) {
    // taggable
    if (val._value == null) {
      // nullable
      return val;
    }
    outerTag = val._tag;
    val = val._value;
  }
  if (_.isArray(val)) {
    // mappable
    const res: any[] = [];
    for (const v of val) {
      if (v == null) {
        // nullable
        res.push(null);
      } else if (Types.isConcreteTaggedValue(v)) {
        // taggable
        if (v._value == null) {
          // nullable
          res.push(v);
        } else {
          res.push(Types.concreteTaggedValue(v._tag, await applyFn(v._value)));
        }
      } else {
        res.push(await applyFn(v));
      }
    }
    val = res;
  } else {
    val = await applyFn(val);
  }
  if (outerTag !== undefined) {
    return Types.concreteTaggedValue(outerTag, val);
  } else {
    return val;
  }
};

export const mappableSkipTaggable = (
  type: Types.Type,
  applyFn: (inType: Types.Type) => Types.Type
): Types.Type => {
  return skipTaggable(type, t => mappable(t, u => skipTaggable(u, applyFn)));
};

export const mappableSkipTaggableAsync = (
  type: Types.Type,
  applyFn: (inType: Types.Type) => Promise<Types.Type>
): Promise<Types.Type> => {
  return skipTaggableAsync(type, t =>
    mappableAsync(t, u => skipTaggableAsync(u, applyFn))
  );
};

export const mappableNullableSkipTaggable = (
  type: Types.Type,
  applyFn: (inType: Types.Type) => Types.Type
): Types.Type => {
  return nullableSkipTaggable(type, t =>
    mappable(t, u => nullableSkipTaggable(u, applyFn))
  );
};

export const mappableNullableSkipTaggableAsync = (
  type: Types.Type,
  applyFn: (inType: Types.Type) => Promise<Types.Type>
): Promise<Types.Type> => {
  return nullableSkipTaggableAsync(type, t =>
    mappableAsync(t, u => nullableSkipTaggableAsync(u, applyFn))
  );
};

export const mappableNullableSkipTaggableValAsync = (
  val: any,
  applyFn: (
    inVal: any,
    withTags?: any,
    mapIndex?: number | undefined
  ) => Promise<any>
): Promise<any> => {
  return nullableSkipTaggableValAsync(val, (t, tWithTags) =>
    mappableValAsync(t, (u, didMap, mapIndex) =>
      nullableSkipTaggableValAsync(u, (v, vWithTags) =>
        applyFn(v, didMap ? vWithTags : tWithTags, mapIndex)
      )
    )
  );
};

export const standardOpType = (
  type: Types.Type,
  applyFn: (inType: Types.Type) => Types.Type
) => {
  return mappableNullableTaggable(type, applyFn);
};

export const standardOpValue = (val: any, applyFn: (inVal: any) => any) => {
  return mappableNullableTaggableVal(val, applyFn);
};

// Union and Tagged Value are under-specified
// They should be a union of objects or tagged value of object
export const objectKeyType = (
  type: Types.TypedDictType | Types.Union,
  key: string
): Types.Type => {
  const keyType = Types.typedDictPropertyTypes(type)[key];
  if (keyType == null) {
    return 'none';
  }
  return keyType;
};

export const objectKeyVal = (val: any, key: string) => {
  if (val[key] == null) {
    return null;
  }
  return val[key];
};

export const typedDictPathType = (
  type: Types.Type,
  path: string[]
): Types.Type => {
  return nullableTaggable(type, t => {
    if (!Types.isTypedDictLike(t)) {
      return 'none';
    }
    const key = path[0];
    if (key === '*') {
      const newPropertyTypes: {[key: string]: any} = {};
      for (const [subKey, subVal] of Object.entries(
        Types.typedDictPropertyTypes(t)
      )) {
        if (subVal == null) {
          throw new Error('invalid');
        }
        newPropertyTypes[subKey] = typedDictOrListPathType(
          subVal,
          path.slice(1)
        );
      }
      return Types.typedDict(newPropertyTypes);
    }
    const keyType = objectKeyType(t, path[0]);
    if (path.length > 1) {
      return typedDictOrListPathType(keyType, path.slice(1));
    }
    return keyType;
  });
};

const typedDictOrListPathType = (
  type: Types.Type,
  path: string[]
): Types.Type => {
  return nullableTaggable(type, t => {
    if (!Types.isTypedDictLike(t) && !Types.isListLike(t)) {
      return 'none';
    }
    if (Types.isTypedDictLike(t)) {
      return typedDictPathType(t, path);
    } else if (Types.isListLike(t) && path[0] === '*') {
      const listObjectType = Types.listObjectType(t);
      if (path.length === 1) {
        return {
          type: 'list',
          objectType: listObjectType,
          maxLength: t.maxLength ?? undefined,
          minLength: t.minLength ?? undefined,
        };
      } else {
        return {
          type: 'list',
          objectType: typedDictOrListPathType(listObjectType, path.slice(1)),
          maxLength: t.maxLength ?? undefined,
          minLength: t.minLength ?? undefined,
        };
      }
    } else {
      return 'none';
    }
  });
};

export const typedDictPathVal = (val: any, path: string[]): any => {
  const res = nullableTaggableVal(val, t => {
    if (!_.isObject(t)) {
      return null;
    }
    const key = path[0];
    if (key === '*') {
      const newObj: {[key: string]: any} = {};
      for (const subKey of Object.keys(t)) {
        newObj[subKey] = typedDictOrListPathVal(
          (t as any)[subKey],
          path.slice(1)
        );
      }
      return newObj;
    }
    const keyVal = objectKeyVal(t, path[0]);
    if (keyVal == null) {
      return null;
    }
    if (path.length > 1) {
      return typedDictOrListPathVal(keyVal, path.slice(1));
    }
    return keyVal;
  });
  return res;
};

const typedDictOrListPathVal = (val: any, path: string[]): any => {
  return nullableTaggableVal(val, t => {
    if (!_.isObject(t) && !_.isArray(t)) {
      return null;
    }
    if (_.isArray(t) && path[0] === '*') {
      if (path.length === 1) {
        return t;
      } else {
        return t.map(item => typedDictOrListPathVal(item, path.slice(1)));
      }
    } else if (_.isObject(t)) {
      return typedDictPathVal(t, path);
    } else {
      return 'none';
    }
  });
};

export const getTagFromValue = (val: any) => {
  const tag = val._tag;
  if (Types.isConcreteTaggedValue(tag)) {
    return tag._value;
  }
  return tag;
};

export const getValueFromTaggedValue = (val: any) => {
  return Types.isConcreteTaggedValue(val) ? val._value : val;
};

export const getTagFromTaggedValue = (val: any) => {
  return Types.isConcreteTaggedValue(val) ? val._tag : null;
};

export const getNamedTagFromValue = (val: any, name: string): any => {
  if (val == null) {
    throw new Error('invalid');
  }
  const tag = val._tag;
  if (Types.isConcreteTaggedValue(tag)) {
    if (name in (tag._value as any)) {
      return tag;
    }
    return getNamedTagFromValue(tag, name);
  } else {
    if (tag != null && name in tag) {
      return tag;
    }
  }
  return null;
};

export const getActualNamedTagFromValue = (val: any, name: string): any => {
  return getValueFromTaggedValue(getNamedTagFromValue(val, name));
};

// mntTypeApply will apply a `fn` to the `type` with the following rules:
// - the type will be "mapped" down to `dims` dimension (defaulting to 0 - unit dimension).
//   Note that if the input type does not have at least `dim` dimensions, error is thrown.
// - if `tags` is set to `true`, then the tags will be maintained through the mapping, else
//   will be removed, and added after the function is applied
// - if `nones` is set to `true`, then the 'none' type will be passed to the function,
//   else it will be skipped.
// Note: if there are unions in the type tree, the fn will be applied to each member of the union.
// This is different than the other mappable/nullable/taggable functions.
export type TypeProcessingConfig = {
  dims?: number;
  tags?: boolean;
  nones?: boolean;
};
export const mntTypeApply = (
  type: Types.Type,
  fn: (type: Types.Type) => Types.Type,
  config?: TypeProcessingConfig
): Types.Type => {
  const targetDims = config?.dims ?? 0;
  let targetListDepth: number | null = null;
  if (targetDims > 0) {
    const typeDimDepth = getTypeDimDepth(type);
    if (typeDimDepth < targetDims) {
      throw new TypeError('TypeApply: Type does not have enough dimensions');
    }
    targetListDepth = typeDimDepth - targetDims;
  }
  return mntTypeApplyHelper(
    type,
    fn,
    targetListDepth,
    config?.tags ?? false,
    config?.nones ?? false,
    null,
    {cache: null}
  );
};

export const mntTypeApplyAsync = async (
  type: Types.Type,
  fn: (type: Types.Type) => Promise<Types.Type>,
  config?: TypeProcessingConfig
): Promise<Types.Type> => {
  const targetDims = config?.dims ?? 0;
  let targetListDepth: number | null = null;
  if (targetDims > 0) {
    const typeDimDepth = getTypeDimDepth(type);
    if (typeDimDepth < targetDims) {
      throw new TypeError('ApplyAsync: Type does not have enough dimensions');
    }
    targetListDepth = typeDimDepth - targetDims;
  }
  return mntTypeApplyAsyncHelper(
    type,
    fn,
    targetListDepth,
    config?.tags ?? false,
    config?.nones ?? false,
    null,
    {cache: null}
  );
};

export const mntTypeStrip = (
  type: Types.Type,
  config?: TypeProcessingConfig
): Types.Type => {
  const targetDims = config?.dims ?? 0;
  let targetListDepth: number | null = null;
  if (targetDims > 0) {
    const typeDimDepth = getTypeDimDepth(type);
    if (typeDimDepth < targetDims) {
      throw new TypeError('TypeStrip: Type does not have enough dimensions');
    }
    targetListDepth = typeDimDepth - targetDims;
  }
  return mntTypeStripHelper(
    type,
    targetListDepth,
    config?.tags ?? false,
    config?.nones ?? false
  );
};

export const mntValueApply = (
  value: any,
  fn: (value: any) => any,
  config?: TypeProcessingConfig
): any => {
  const targetDims = config?.dims ?? 0;
  let targetListDepth: number | null = null;
  if (targetDims > 0) {
    const typeDimDepth = getValueDimDepth(value);
    if (typeDimDepth != null) {
      if (typeDimDepth < targetDims) {
        throw new TypeError('ValueApply: Type does not have enough dimensions');
      }
      targetListDepth = typeDimDepth - targetDims;
    }
  }
  return mntValueApplyHelper(
    value,
    fn,
    targetListDepth,
    config?.tags ?? false,
    config?.nones ?? false,
    null,
    {cache: null, valid: false}
  );
};

export const mntValueApplyAsync = async (
  value: any,
  fn: (value: any) => Promise<any>,
  config?: TypeProcessingConfig
): Promise<any> => {
  const targetDims = config?.dims ?? 0;
  let targetListDepth: number | null = null;
  if (targetDims > 0) {
    const typeDimDepth = getValueDimDepth(value);
    if (typeDimDepth != null) {
      if (typeDimDepth < targetDims) {
        throw new TypeError(
          'ValueApplyAsync: Type does not have enough dimensions'
        );
      }
      targetListDepth = typeDimDepth - targetDims;
    }
  }
  return mntValueApplyAsyncHelper(
    value,
    fn,
    targetListDepth,
    config?.tags ?? false,
    config?.nones ?? false,
    null,
    {cache: null, valid: false}
  );
};

const getTypeDimDepth = (type: Types.Type): number => {
  if (Types.isTaggedValue(type)) {
    return getTypeDimDepth(type.value);
  } else if (Types.isUnion(type)) {
    const unionDims = _.uniq(
      type.members.filter(m => m !== 'none').map(getTypeDimDepth)
    );
    if (unionDims.length !== 1) {
      throw new TypeError('Type has ambiguous number of dimensions');
    }
    return unionDims[0];
  } else if (Types.isList(type)) {
    return getTypeDimDepth(type.objectType) + 1;
  } else {
    return 0;
  }
};

const getValueDimDepth = (value: any): number | null => {
  if (Types.isConcreteTaggedValue<any, any>(value)) {
    return getValueDimDepth(value._value);
  } else if (_.isArray(value)) {
    let innerDim: number | null = 0;
    for (const v of value) {
      const potentialInnerDim = getValueDimDepth(v);
      // Settle on the first known dimension. Ideally we would
      // look at every element, but that would be too expensive.
      if (potentialInnerDim !== null) {
        innerDim = potentialInnerDim;
        break;
      }
    }
    return innerDim + 1;
  } else if (value == null) {
    return null;
  } else {
    return 0;
  }
};

const mntTypeApplyHelper = (
  type: Types.Type,
  fn: (type: Types.Type) => Types.Type,
  listDepth: number | null,
  tags: boolean,
  nones: boolean,
  ancestorTag: Types.Type | null,
  noneTypeResult: {cache: Types.Type | null}
): Types.Type => {
  if (Types.isTaggedValue(type)) {
    // If it is a tagged value, unwrap it, but maintain the tag as an ancestor.
    if (tags) {
      return mntTypeApplyHelper(
        type.value,
        fn,
        listDepth,
        tags,
        nones,
        Types.taggedValue(ancestorTag, type.tag),
        noneTypeResult
      );
    } else {
      return Types.taggedValue(
        Types.taggedValue(ancestorTag, type.tag),
        mntTypeApplyHelper(
          type.value,
          fn,
          listDepth,
          tags,
          nones,
          null,
          noneTypeResult
        )
      );
    }
  } else if (Types.isList(type) && (listDepth == null || listDepth > 0)) {
    // If it is a list, unwrap it, but maintain the list as an ancestor.
    const newListDepth = listDepth != null ? listDepth - 1 : null;
    return Types.list(
      mntTypeApplyHelper(
        type.objectType,
        fn,
        newListDepth,
        tags,
        nones,
        ancestorTag,
        noneTypeResult
      ),
      type.minLength,
      type.maxLength
    );
  } else if (Types.isUnion(type)) {
    // If it is a union, apply the function to each member, then union the results.
    return Types.union(
      type.members.map(m => {
        return mntTypeApplyHelper(
          m,
          fn,
          listDepth,
          tags,
          nones,
          ancestorTag,
          noneTypeResult
        );
      })
    );
  } else if (nones || type !== 'none') {
    // for valid types, wrap the ancestor tag, and lists appropriately before applying function
    if (tags) {
      type = Types.taggedValue(ancestorTag, type);
      ancestorTag = null;
    }
    if (type === 'none') {
      if (noneTypeResult.cache == null) {
        noneTypeResult.cache = fn(type);
      }
      type = noneTypeResult.cache;
    } else {
      type = fn(type);
    }
  }

  // Finally, rewrap the final type on the way out:
  type = Types.taggedValue(ancestorTag, type);

  return type;
};

const mntTypeApplyAsyncHelper = async (
  type: Types.Type,
  fn: (type: Types.Type) => Promise<Types.Type>,
  listDepth: number | null,
  tags: boolean,
  nones: boolean,
  ancestorTag: Types.Type | null,
  noneTypeResult: {cache: Types.Type | null}
): Promise<Types.Type> => {
  if (Types.isTaggedValue(type)) {
    // If it is a tagged value, unwrap it, but maintain the tag as an ancestor.
    if (tags) {
      return mntTypeApplyAsyncHelper(
        type.value,
        fn,
        listDepth,
        tags,
        nones,
        Types.taggedValue(ancestorTag, type.tag),
        noneTypeResult
      );
    } else {
      return Types.taggedValue(
        Types.taggedValue(ancestorTag, type.tag),
        await mntTypeApplyAsyncHelper(
          type.value,
          fn,
          listDepth,
          tags,
          nones,
          null,
          noneTypeResult
        )
      );
    }
  } else if (Types.isList(type) && (listDepth == null || listDepth > 0)) {
    // If it is a list, unwrap it, but maintain the list as an ancestor.
    const newListDepth = listDepth != null ? listDepth - 1 : null;
    return Types.list(
      await mntTypeApplyAsyncHelper(
        type.objectType,
        fn,
        newListDepth,
        tags,
        nones,
        ancestorTag,
        noneTypeResult
      ),
      type.minLength,
      type.maxLength
    );
  } else if (Types.isUnion(type)) {
    // If it is a union, apply the function to each member, then union the results.
    return Types.union(
      await Promise.all(
        type.members.map(m => {
          return mntTypeApplyAsyncHelper(
            m,
            fn,
            listDepth,
            tags,
            nones,
            ancestorTag,
            noneTypeResult
          );
        })
      )
    );
  } else if (nones || type !== 'none') {
    // for valid types, wrap the ancestor tag, and lists appropriately before applying function
    if (tags) {
      type = Types.taggedValue(ancestorTag, type);
      ancestorTag = null;
    }
    if (type === 'none') {
      if (noneTypeResult.cache == null) {
        noneTypeResult.cache = await fn(type);
      }
      type = noneTypeResult.cache;
    } else {
      type = await fn(type);
    }
  }

  // Finally, rewrap the final type on the way out:
  type = Types.taggedValue(ancestorTag, type);

  return type;
};

const mntTypeStripHelper = (
  type: Types.Type,
  listDepth: number | null,
  tags: boolean,
  nones: boolean
): Types.Type => {
  if (Types.isTaggedValue(type)) {
    // If it is a tagged value, unwrap it, but maintain the tag as an ancestor.
    const res = mntTypeStripHelper(type.value, listDepth, tags, nones);
    return tags ? Types.taggedValue(type.tag, res) : res;
  } else if (Types.isList(type) && (listDepth == null || listDepth > 0)) {
    // If it is a list, unwrap it, but maintain the list as an ancestor.
    const newListDepth = listDepth != null ? listDepth - 1 : null;
    return mntTypeStripHelper(type.objectType, newListDepth, tags, nones);
  } else if (Types.isUnion(type)) {
    // If it is a union, apply the function to each member, then union the results.
    const members = type.members
      .filter(m => {
        return (
          nones ||
          (m !== 'none' && !(Types.isTaggedValue(m) && m.value === 'none'))
        );
      })
      .map(m => {
        return mntTypeStripHelper(m, listDepth, tags, nones);
      });
    return members.length > 0 ? Types.union(members) : 'none';
  }

  return type;
};

const mntValueApplyHelper = (
  value: any,
  fn: (value: any) => any,
  listDepth: number | null,
  tags: boolean,
  nones: boolean,
  ancestorTag: Types.ConcreteTaggedValue | null,
  noneTypeResult: {cache: any; valid: boolean}
): any => {
  if (Types.isConcreteTaggedValue<any, any>(value)) {
    // If it is a tagged value, unwrap it, but maintain the tag as an ancestor.
    if (tags) {
      return mntValueApplyHelper(
        value._value,
        fn,
        listDepth,
        tags,
        nones,
        ancestorTag != null
          ? Types.concreteTaggedValue(ancestorTag, value._tag)
          : value._tag,
        noneTypeResult
      );
    } else {
      return Types.concreteTaggedValue(
        ancestorTag != null
          ? Types.concreteTaggedValue(ancestorTag, value._tag)
          : value._tag,
        mntValueApplyHelper(
          value._value,
          fn,
          listDepth,
          tags,
          nones,
          null,
          noneTypeResult
        )
      );
    }
  } else if (_.isArray(value) && (listDepth == null || listDepth > 0)) {
    // If it is a list, unwrap it, but maintain the list as an ancestor.
    const newListDepth = listDepth != null ? listDepth - 1 : null;
    return value.map(v =>
      mntValueApplyHelper(
        v,
        fn,
        newListDepth,
        tags,
        nones,
        ancestorTag,
        noneTypeResult
      )
    );
  } else if (nones || value != null) {
    // for valid types, wrap the ancestor tag, and lists appropriately before applying function
    if (tags && ancestorTag != null) {
      value = Types.concreteTaggedValue(ancestorTag, value);
      ancestorTag = null;
    }
    if (value === 'none') {
      if (!noneTypeResult.valid) {
        noneTypeResult.cache = fn(value);
        noneTypeResult.valid = true;
      }
      value = noneTypeResult.cache;
    } else {
      value = fn(value);
    }
  }

  // Finally, rewrap the final type on the way out:
  if (ancestorTag != null) {
    value = Types.concreteTaggedValue(ancestorTag, value);
  }

  return value;
};

const mntValueApplyAsyncHelper = async (
  value: any,
  fn: (value: any) => Promise<any>,
  listDepth: number | null,
  tags: boolean,
  nones: boolean,
  ancestorTag: Types.ConcreteTaggedValue | null,
  noneTypeResult: {cache: any; valid: boolean}
): Promise<any> => {
  if (Types.isConcreteTaggedValue<any, any>(value)) {
    // If it is a tagged value, unwrap it, but maintain the tag as an ancestor.
    if (tags) {
      return mntValueApplyAsyncHelper(
        value._value,
        fn,
        listDepth,
        tags,
        nones,
        ancestorTag != null
          ? Types.concreteTaggedValue(ancestorTag, value._tag)
          : value._tag,
        noneTypeResult
      );
    } else {
      return Types.concreteTaggedValue(
        ancestorTag != null
          ? Types.concreteTaggedValue(ancestorTag, value._tag)
          : value._tag,
        await mntValueApplyAsyncHelper(
          value._value,
          fn,
          listDepth,
          tags,
          nones,
          null,
          noneTypeResult
        )
      );
    }
  } else if (_.isArray(value) && (listDepth == null || listDepth > 0)) {
    // If it is a list, unwrap it, but maintain the list as an ancestor.
    const newListDepth = listDepth != null ? listDepth - 1 : null;
    return Promise.all(
      value.map(v =>
        mntValueApplyAsyncHelper(
          v,
          fn,
          newListDepth,
          tags,
          nones,
          ancestorTag,
          noneTypeResult
        )
      )
    );
  } else if (nones || value != null) {
    // for valid types, wrap the ancestor tag, and lists appropriately before applying function
    if (tags && ancestorTag != null) {
      value = Types.concreteTaggedValue(ancestorTag, value);
      ancestorTag = null;
    }
    if (value === 'none') {
      if (!noneTypeResult.valid) {
        noneTypeResult.cache = fn(value);
        noneTypeResult.valid = true;
      }
      value = noneTypeResult.cache;
    } else {
      value = await fn(value);
    }
  }

  // Finally, rewrap the final type on the way out:
  if (ancestorTag != null) {
    value = Types.concreteTaggedValue(ancestorTag, value);
  }

  return value;
};

// TODO: mntValueStrip
